import * as THREE from 'three'
import CANNON from 'cannon'

export default class BrickBreaking
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.scene = _options.scene
        this.object = _options.object // The brick object to break
        this.physics = _options.physics
        this.resources = _options.resources
        this.debug = _options.debug

        // Set up
        this.container = new THREE.Object3D()
        this.faces = []

        // Get the single brick break texture
        this.brickBreakTexture = null
        if(this.resources && this.resources.items && this.resources.items.brickBreakTexture)
        {
            this.brickBreakTexture = this.resources.items.brickBreakTexture
        }

        this.setupBreaking()
    }

    setupBreaking()
    {
        // Get the overall bounding box of the entire brick object (not individual meshes)
        const box = new THREE.Box3().setFromObject(this.object.container)
        const size = {
            x: box.max.x - box.min.x,
            y: box.max.y - box.min.y,
            z: box.max.z - box.min.z
        }
        const center = new THREE.Vector3()
        box.getCenter(center)

        // Use the single brick break texture for all faces

        // Position container at origin (faces will be in world space)
        this.container.position.set(0, 0, 0)
        this.container.rotation.set(0, 0, 0)
        this.container.scale.set(1, 1, 1)

        // Create exactly 6 faces for the cube
        const faceConfigs = [
            // Front face (Z+)
            { normal: new THREE.Vector3(0, 0, 1), position: new THREE.Vector3(center.x, center.y, box.max.z), size: { width: size.x, height: size.y } },
            // Back face (Z-)
            { normal: new THREE.Vector3(0, 0, -1), position: new THREE.Vector3(center.x, center.y, box.min.z), size: { width: size.x, height: size.y } },
            // Right face (X+)
            { normal: new THREE.Vector3(1, 0, 0), position: new THREE.Vector3(box.max.x, center.y, center.z), size: { width: size.z, height: size.y } },
            // Left face (X-)
            { normal: new THREE.Vector3(-1, 0, 0), position: new THREE.Vector3(box.min.x, center.y, center.z), size: { width: size.z, height: size.y } },
            // Top face (Y+)
            { normal: new THREE.Vector3(0, 1, 0), position: new THREE.Vector3(center.x, box.max.y, center.z), size: { width: size.x, height: size.z } },
            // Bottom face (Y-)
            { normal: new THREE.Vector3(0, -1, 0), position: new THREE.Vector3(center.x, box.min.y, center.z), size: { width: size.x, height: size.z } }
        ]

        faceConfigs.forEach((faceConfig, index) => {
            // Create a plane geometry for each face
            const faceGeometry = new THREE.PlaneGeometry(faceConfig.size.width, faceConfig.size.height)
            
            // Use the single brick break texture
            const texture = this.brickBreakTexture
            if(texture)
            {
                texture.flipY = false
            }
            const faceMaterial = new THREE.MeshStandardMaterial({
                map: texture || null,
                color: 0xffffff,
                side: THREE.FrontSide,
                transparent: true,
                alphaTest: 0.1
            })
            
            // Create face mesh with random texture
            const face = new THREE.Mesh(faceGeometry, faceMaterial)
            
            // Position face in world space (already calculated from bounding box)
            face.position.copy(faceConfig.position)
            
            // Create a matrix to orient the face based on its normal
            // The face should face outward (normal direction)
            const up = new THREE.Vector3(0, 1, 0)
            const right = new THREE.Vector3()
            right.crossVectors(faceConfig.normal, up).normalize()
            if(right.length() < 0.1) {
                // If normal is parallel to up, use different up vector
                up.set(0, 0, 1)
                right.crossVectors(faceConfig.normal, up).normalize()
            }
            up.crossVectors(right, faceConfig.normal).normalize()
            
            const faceMatrix = new THREE.Matrix4()
            // Face should look in the direction of the normal (outward)
            faceMatrix.makeBasis(right, up, faceConfig.normal.clone().negate())
            face.rotation.setFromRotationMatrix(faceMatrix)
            
            // Scale planes up by factor of 2
            face.scale.set(2, 2, 1)
            
            // Store original transform and normal
            face.userData.originalPosition = face.position.clone()
            face.userData.originalRotation = face.rotation.clone()
            face.userData.normal = faceConfig.normal.clone()
            face.userData.meshIndex = index

            this.faces.push(face)
            this.container.add(face)
        })

        // Hide original brick
        this.object.container.visible = false
        
        // Create physics bodies for each face
        this.createPhysicsBodies()
    }

    createPhysicsBodies()
    {
        // Get original mass - use same approach as letters (simple and works)
        const originalMass = this.object.collision ? this.object.collision.body.mass : 0.5
        const faceMass = originalMass / 6 // Same as letters - simple division
        
        // Create physics bodies for each face
        this.faces.forEach((face) => {
            // Get face size from geometry
            face.geometry.computeBoundingBox()
            const box = face.geometry.boundingBox
            const size = {
                width: box.max.x - box.min.x,
                height: box.max.y - box.min.y,
                depth: 0.1 // Thin box for the face
            }
            
            // Create physics body for this face
            const halfExtents = new CANNON.Vec3(size.width * 0.5, size.height * 0.5, size.depth * 0.5)
            const shape = new CANNON.Box(halfExtents)
            
            const body = new CANNON.Body({ mass: faceMass })
            body.addShape(shape)
            
            // Set position and rotation from face
            body.position.set(face.position.x, face.position.y, face.position.z)
            const quaternion = new THREE.Quaternion().setFromEuler(face.rotation)
            body.quaternion.set(quaternion.x, quaternion.y, quaternion.z, quaternion.w)
            
            // Add to physics world
            this.physics.world.addBody(body)
            
            // Store reference
            face.userData.physicsBody = body
            face.userData.isInactive = false
            face.userData.hasHitSomething = false
            face.userData.impactTime = null
            
            // Listen for any collision
            body.addEventListener('collide', (event) => {
                // On any collision, mark impact time
                if(!face.userData.hasHitSomething)
                {
                    face.userData.hasHitSomething = true
                    face.userData.impactTime = this.time.elapsed
                }
            })
            
            // Update face position from physics and check if should disappear
            this.time.on('tick', () => {
                // Update position only if still active (has physics body)
                if(!face.userData.isInactive)
                {
                    face.position.set(body.position.x, body.position.y, body.position.z)
                    face.quaternion.set(body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w)
                    
                    // Check if lifetime has passed since impact
                    // time.elapsed is in milliseconds, so convert to seconds by dividing by 1000
                    if(face.userData.hasHitSomething && face.userData.impactTime !== null)
                    {
                        const timeSinceImpact = (this.time.elapsed - face.userData.impactTime) / 1000 // Convert to seconds
                        const faceLifetime = 0.05 // Lifetime in seconds (change this value to adjust how long pieces stay)
                        if(timeSinceImpact >= faceLifetime)
                        {
                            // Remove face after lifetime expires
                            face.userData.isInactive = true
                            
                            // Remove physics body
                            this.physics.world.removeBody(body)
                            
                            // Remove from container and dispose
                            this.container.remove(face)
                            face.geometry.dispose()
                            if(face.material.dispose)
                            {
                                face.material.dispose()
                            }
                            if(face.material.map && face.material.map.dispose)
                            {
                                face.material.map.dispose()
                            }
                        }
                    }
                }
            })
        })
    }

    break()
    {
        // No animation needed - faces are already created and physics is set up
        // Just add container to scene if not already added
        if(this.container.parent === null)
        {
            if(this.scene)
            {
                this.scene.add(this.container)
            }
        }
    }

    dispose()
    {
        this.faces.forEach(face => {
            face.geometry.dispose()
            if(face.material.dispose)
            {
                face.material.dispose()
            }
        })
        this.faces = []
    }
}

